import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldComponent } from '../../../types/FieldComponent';
import { TimeFilter } from '../filters/TimeFilter';
import {
  Button as MUIButton,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { debounce } from 'lodash';
import api from '../../../services/api';
import { MoreOptions } from '../assets/MoreOptions';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import { SPSelectField } from './SPSelectField';
import { enumItems } from '../../../schema/enumItems';
import { service } from '../../../schema/service';
import { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { v4 as uuidv4 } from 'uuid';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';

const countOpenDialogs = () => {
  return document.querySelectorAll('.MuiDialog-root').length;
};

export const CustomFieldForServices: FieldComponent<any, any> = ({
  onChange,
  isActive,
  value,
  defaultValue,
  allValue,
  hasTooltip,
  disabled,
  column,
}) => {
  const [properties, setProperties] = useState<any>(
    defaultValue ? defaultValue : []
  );

  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [openAddNewRow, setOpenAddNewRow] = useState<any>(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const draggablePaneId = useMemo(() => {
    return `draggable-responsive-add-new-dialog-${uuidv4()}`;
  }, []);

  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: !countOpenDialogs() ? 0 : 35 * countOpenDialogs(),
    y: !countOpenDialogs() ? 0 : -20 * countOpenDialogs(),
  });

  const handleDrag = (e: any, data: any) => {
    setPosition({ x: data.x, y: data.y });
  };

  const handleOpenAddNewForm = (value: boolean) => {
    setOpenAddNewRow(value);
    setSelectedService(null);
    setSelectedProvider(null);
  };

  const handleSubmit = () => {
    const propertiesTemp: any = [...properties];

    const selectedServiceTemp: any = { ...selectedService };
    selectedServiceTemp.service_provider = selectedProvider;

    propertiesTemp.push(selectedServiceTemp);

    onChange(propertiesTemp);
    setProperties(propertiesTemp);
    handleOpenAddNewForm(false);
  };

  const handleDeleteService = (index: number) => {
    const propertiesTemp: any = properties.filter(
      (item: any, itemIndex: number) => itemIndex !== index
    );
    onChange(propertiesTemp);
    setProperties(propertiesTemp);
    handleOpenAddNewForm(false);
  };

  const [currentState, setCurrentState] = useState<any>({
    value: {},
    send: false,
  });

  const debouncedChange = useCallback(
    debounce((newValue) => {
      onChange(newValue);
    }, 500),
    []
  );

  const fetchServices = useCallback(() => {
    if (allValue?.id) {
      const searchParams = new URLSearchParams({});

      const prices = api
        .get(`/service-packages/${allValue?.id}`, searchParams)
        .then((res: any) => {
          setProperties(res?.services ? res.services : []);
        });
    } else {
      setProperties([]);
    }
  }, [allValue]);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (currentState.send) {
      debouncedChange(currentState.value);
    }
  }, [currentState, debouncedChange]);

  const PaperComponent = useCallback(
    (props: PaperProps) => {
      return (
        <Draggable
          handle={`#${draggablePaneId}`}
          cancel={'[class*="MuiDialogContent-root"], .non-draggable-button'}
          position={position}
          onStop={handleDrag}
        >
          <Paper {...props} />
        </Draggable>
      );
    },
    [position, draggablePaneId]
  );

  return (
    <>
      <Box
        sx={{
          p: '10px',
        }}
      >
        <Typography>Services</Typography>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: '0px',
            width: '100% !important',
          }}
        >
          <Table size="small">
            <TableHead
              sx={{
                '& .MuiTableCell-root': {
                  color: 'inherit',
                  border: 'none',
                },
                '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                  border: 'none',
                  fontSize: '14px',
                  p: '10px',
                },
                background: '#f1f1f1',
                borderBottom: '1px solid #dfdbdb',
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    width: '50px',
                  }}
                ></TableCell>
                <TableCell
                  sx={{
                    minWidth: '150px',
                  }}
                >
                  Service Provider
                </TableCell>
                <TableCell
                  sx={{
                    minWidth: '150px',
                  }}
                >
                  Service
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties &&
                properties.map((serviceItem: any, index: number) => {
                  return (
                    <TableRow>
                      <TableCell
                        sx={{
                          padding: '5px',
                        }}
                      >
                        <MoreOptions
                          hideEdit={true}
                          clickOnEdit={() => {}}
                          clickOnDelete={() => {
                            handleDeleteService(index);
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          padding: '5px',
                        }}
                      >
                        {serviceItem?.service_provider?.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: '5px',
                        }}
                      >
                        {serviceItem?.name}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Tooltip
          title={`Add new`}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -14],
                  },
                },
              ],
            },
          }}
        >
          <IconButton
            color={'primary'}
            onClick={() => {
              handleOpenAddNewForm(true);
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>

        {openAddNewRow && (
          <Dialog
            PaperComponent={PaperComponent}
            id={draggablePaneId}
            fullScreen={fullScreen}
            open={true}
            onClose={(event) => {
              handleOpenAddNewForm(false);
            }}
            aria-labelledby="responsive-dialog-title"
            maxWidth={'md'}
            sx={{
              '& .MuiPaper-root': {
                width: {
                  md: 'auto',
                  lg: 'auto',
                },
                minWidth: {
                  md: '600px',
                  lg: '600px',
                },

                maxWidth: {
                  md: 'calc(100vw - 400px)',
                  lg: 'calc(100vw - 400px)',
                },
              },
              '& .MuiInputBase-root.MuiOutlinedInput-root': {
                borderRadius: '0px',
              },
              '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '& .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#b3cdcae0',
              },
            }}
          >
            <DialogTitle
              sx={{
                borderBottom: '1px solid #b3cdcae0',
              }}
            >
              Add new Service
            </DialogTitle>
            <DialogContent
              sx={{
                padding: '0px',
              }}
            >
              <Stack
                direction="row"
                spacing={0}
                sx={{
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    borderBottom: '1px solid #dfdbdb',
                    borderRight: '1px solid #dfdbdb',
                    borderLeft: '1px solid #dfdbdb',
                    display: 'flex',
                    alignItems: 'center',
                    background: '#f1f1f1',
                    fontSize: {
                      xs: '15px',
                    },
                  }}
                >
                  <span className={'px-[8px]'}>Service Provider</span>
                </Box>
                <Box
                  sx={{
                    flex: 2,
                    borderBottom: '1px solid #dfdbdb',
                    borderRight: '1px solid #dfdbdb',
                    '& .MuiFormControl-root.MuiTextField-root': {
                      width: '100%',
                    },
                  }}
                >
                  <SPSelectField
                    onChange={(value) => {
                      setSelectedProvider(value);
                    }}
                    schema={enumItems.serviceProvider}
                    defaultValue={null}
                    forcedValue={null}
                    disabled={false}
                    isMissing={false}
                    accordingToValue={null}
                  />
                </Box>
              </Stack>
              <Stack
                direction="row"
                spacing={0}
                sx={{
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    borderBottom: '1px solid #dfdbdb',
                    borderRight: '1px solid #dfdbdb',
                    borderLeft: '1px solid #dfdbdb',
                    display: 'flex',
                    alignItems: 'center',
                    background: '#f1f1f1',
                    fontSize: {
                      xs: '15px',
                    },
                  }}
                >
                  <span className={'px-[8px]'}>Service</span>
                </Box>
                <Box
                  sx={{
                    flex: 2,
                    borderBottom: '1px solid #dfdbdb',
                    borderRight: '1px solid #dfdbdb',
                    '& .MuiFormControl-root.MuiTextField-root': {
                      width: '100%',
                    },
                  }}
                >
                  <SPSelectField
                    onChange={(value) => {
                      setSelectedService(value);
                    }}
                    schema={service}
                    defaultValue={null}
                    forcedValue={null}
                    disabled={false}
                    isMissing={false}
                    accordingToValue={
                      selectedProvider
                        ? {
                            name: 'service_provider',
                            id: selectedProvider.id,
                          }
                        : null
                    }
                  />
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <MUIButton
                onClick={() => {
                  handleOpenAddNewForm(false);
                }}
              >
                Cancel
              </MUIButton>
              <MUIButton
                variant={'contained'}
                color={'primary'}
                onClick={() => {
                  handleSubmit();
                }}
              >
                SUBMIT
              </MUIButton>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </>
  );
};

CustomFieldForServices.includes = (query, value) => true;

CustomFieldForServices.comparator = () => 0;

CustomFieldForServices.FilterRenderer = TimeFilter;
